import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

@Component({
  selector: 'select-input',
  template: `
    <div class="mb-4 form-group">
      <label
        *ngIf="label !== ''"
        [for]="controlName"
        class="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"
      >
        {{ label }}
        <span *ngIf="isRequired() && label !== ''" class="text-red-500">*</span>

        <!-- Tooltip Icon (only if tooltipText is provided) -->
        <mat-icon
          *ngIf="tooltipText"
          svgIcon="mat_outline:info"
          [matTooltip]="tooltipText"
          [matTooltipPosition]="tooltipPosition"
          [matTooltipShowDelay]="showDelay"
          [matTooltipHideDelay]="hideDelay"
          class="text-grey-500 cursor-pointer icon-size-5"
        ></mat-icon>
      </label>

      <!-- Wrapper to align select and clear button side by side -->
      <div class="flex items-center gap-2">
        <!-- FORM STYLE SELECT -->
        <select
          *ngIf="inputStyle === 'form'"
          [formControl]="controlName"
          (change)="onSelect.emit(controlName.value)"
          [id]="controlName"
          [name]="controlName"
          class="h-8 w-full border text-black border-gray-300 px-4 rounded-md form-control"
          [ngClass]="{ 'bg-gray-100': controlName.disabled }"
          [class.is-invalid]="controlName.invalid && controlName.touched"
        >
          <option *ngIf="showPlaceholder" [value]="null" disabled>
            {{ placeholder }}
          </option>
          <option
            *ngFor="let option of options; index as index"
            [ngValue]="optionValue ? option[optionValue] : option.toLowerCase()"
            [disabled]="
              disabledOptions.includes(
                optionValue ? option[optionValue] : option.toLowerCase()
              )
            "
          >
            {{ optionLabel ? option[optionLabel] : option }}
          </option>
        </select>

        <!-- HEADER STYLE SELECT -->
        <select
          *ngIf="inputStyle === 'header'"
          [formControl]="controlName"
          (change)="onSelect.emit(controlName.value)"
          [id]="controlName"
          [name]="controlName"
          class="h-9 w-full text-blue-500 font-bold border-gray-300 px-4 rounded-md form-control"
          [ngClass]="{ 'border-[2px] border-blue-500': type === 'primary' }"
          [class.is-invalid]="controlName.invalid && controlName.touched"
        >
          <option *ngIf="showPlaceholder" [value]="null" disabled>
            {{ placeholder }}
          </option>
          <option
            *ngFor="let option of options; index as index"
            [ngValue]="optionValue ? option[optionValue] : option.toLowerCase()"
            [disabled]="
              disabledOptions.includes(
                optionValue ? option[optionValue] : option.toLowerCase()
              )
            "
          >
            {{ optionLabel ? option[optionLabel] : option }}
          </option>
        </select>

        <!-- Clear Button -->
        <button
          *ngIf="hasClear"
          mat-icon-button
          class="text-red-600 text-sm flex items-center gap-1"
          (click)="clearSelection()"
          type="button"
        >
          <mat-icon
            class="text-red-600 icon-size-4.5"
            [svgIcon]="'mat_outline:delete'"
          ></mat-icon>
          <span class="hidden sm:inline">Clear</span>
        </button>
      </div>

      <form-validator [controlName]="controlName"></form-validator>
    </div>
  `,
})
export class SelectInputComponent implements OnInit, OnChanges {
  @Input() label: string = '';
  @Input() type: string = 'none';
  @Input() readOnly: boolean = false;
  @Input() showPlaceholder: boolean = true;
  @Input() placeholder: string = 'Select an option';
  @Input() controlName: FormControl = new FormControl('');
  @Input() options = [];
  @Input() optionValue: string = '';
  @Input() optionLabel: string = '';
  @Input() inputStyle: 'header' | 'form' = 'form';
  @Input() disabledOptions: string[] = [];
  @Input() hasClear: boolean = false;
  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Input() disabled: boolean = false;

  // ✅ Tooltip-related Inputs
  @Input() tooltipText: string = '';
  @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' = 'above';
  @Input() showDelay: number = 50;
  @Input() sort: boolean = false;
  @Input() hideDelay: number = 50;
  @Input() isSettingOption: boolean = false;

  ngOnInit() {
    if (this.readOnly) {
      this.controlName.disable();
    }
    if (this.isSettingOption) {
      this.watchValueAndOptions();
    }
  }
  private watchValueAndOptions(): void {
    // Watch value changes
    this.controlName.valueChanges.subscribe(() => {
      this.ensureValueExistsInOptions();
    });

    // Run once initially
    setTimeout(() => {
      this.ensureValueExistsInOptions();
    });
  }
  ngOnChanges(changes: any): void {
    if (this.disabled) {
      this.controlName.disable();
    } else {
      this.controlName.enable();
    }

    if (
      this.isSettingOption &&
      (changes['options'] || changes['controlName'])
    ) {
      this.ensureValueExistsInOptions();
    }
  }
  private ensureValueExistsInOptions(): void {
    const value = this.controlName?.value;

    if (value === null || value === undefined || value === '') {
      return;
    }

    const exists = this.options.some((option: any) => {
      const optionVal = this.optionValue ? option[this.optionValue] : option;

      return optionVal === value;
    });

    if (!exists) {
      // If using object options
      if (this.optionValue && this.optionLabel) {
        this.options = [
          ...this.options,
          {
            [this.optionValue]: value,
            [this.optionLabel]: value, // fallback label
          },
        ];
      } else {
        // If simple string array
        this.options = [...this.options, value];
      }

      if (this.sort) {
        this.options = [...this.options].sort((a, b) => this.natSort(a, b));
      }
    }
  }
  private natSort(a: any, b: any): number {
    const getValue = (item: any) =>
      this.optionValue ? item[this.optionValue] : item;

    const aStr = String(getValue(a));
    const bStr = String(getValue(b));

    return aStr.localeCompare(bStr, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }
  public isRequired(): boolean {
    return this.controlName.hasValidator(Validators.required);
  }

  public clearSelection(): void {
    this.controlName.setValue(null);
    this.onSelect.emit(null);
  }
}
